import type { ReactNode } from 'react';
import { renderCell, renderHeader } from '../components/table';
import type { HeaderProps } from '../components/table/header';
import { useMemo, useContext, useState, useEffect } from 'react';
import { Flex, Box } from '@chakra-ui/react';
import { BreakdownBy } from '../components/breakdown-by';
import { useDispatch, useSelector } from 'react-redux';
import { applyGroup } from '@/store/atsReports/pipeline';
import { secondsToHumanTime } from '@/utils/secondsToHumanTime';
import { CenterSpinner, Table, ViewMorePopover } from '@/components/ats';
import {
  EmptyDataWrapper,
  isReportTableEmpty,
} from '../components/empty-data-wrapper';
import { ReportSelectionContext } from '@/contextProviders/ReportSelection';
import { useKFlags } from '@/hooks/use-k-flags';
import { useDrillDownHandler } from '../drilldown/use-drilldownHeader';
import { KText } from '@/ui-library/components';
import { EmptyReportChart } from '@/pages/reports/custom-reports/components/ReportCharts/EmptyReportChart';

// ─── Constants ─────────────────────────────────────────────────────────────

interface Option {
  label: string;
  id: number | string;
}

const GROUP_OPTIONS_BASE: Array<Option & { key?: string }> = [
  { id: 'job_name', label: 'Job name' },
  { id: 'primary_recruiter', label: 'Primary recruiter' },
  { id: 'primary_hm', label: 'Primary hiring manager' },
  { id: 'appl_contact_source', label: 'Source' },
  { id: 'appl_credited_to', label: 'Credited to' },
  { id: 'department', label: 'Department' },
  { id: 'office', label: 'Office' },
  { id: 'application_creator', label: 'Application creator' },
  { id: 'bgv_risk_level', label: 'Fraud status' },
];

const LIVE_PIPELINE_METRICS: Option[] = [
  { id: 'time_in_stage', label: 'Time in stage' },
];

const STATIC_STAGES = [
  {
    title: 'Prospect stages',
    accessor: 'prospect_actual_count',
    timeAccessor: 'time_in_prospect',
  },
  {
    title: 'Application review stages',
    accessor: 'application_review_actual_count',
    timeAccessor: 'time_in_application_review',
  },
  {
    title: 'Interview stages',
    accessor: 'interviews_actual_count',
    timeAccessor: 'time_in_interviews',
  },
  {
    title: 'Offer stages',
    accessor: 'offer_actual_count',
    timeAccessor: 'time_in_offer',
  },
  { title: 'Hired stages', accessor: 'hired_actual_count', timeAccessor: null },
];

// ─── Helpers ────────────────────────────────────────────────────────────────

const BGV_RISK_LEVEL_MAP: Record<string, string> = {
  '0': 'Not evaluated',
  '-1': 'Failed',
  '1': 'No issues',
  '2': 'Inconclusive',
  '3': 'Suspicious',
};

const formatBgvRiskLevel = (value: string) =>
  BGV_RISK_LEVEL_MAP[value] || value;

const computeTimeInStage = (latency: number, count: number) =>
  secondsToHumanTime(count ? Math.floor(latency / count) : 0);

// ─── Component ──────────────────────────────────────────────────────────────

export const LiveJobPipelineTable = ({
  metric = 'time_in_stage',
  setMetric,
  isReadOnly = false,
  isPrintView = false,
  groupBy1,
  groupBy2,
  passThroughMetric,
  customData,
  customBreakdownChange,
  renderTitle,
  onDrillDownOpen = () => {},
  customStageMapping = {},
  onSortChange = () => {},
  isEditMode = false,
  tableColStyles = {},
  onTableColDataChange,
  initialTableColData,
  skipLocalStorage = false,
  feReportsV3 = false,
  isFullScreen = false,
  isFilterApplied = false,
}) => {
  const dispatch = useDispatch();
  const { feFraudDetection } = useKFlags();
  const { consumer } = useContext(ReportSelectionContext);
  const [currentTableColData, setCurrentTableColData] =
    useState(initialTableColData);

  const GROUP_OPTIONS = useMemo(
    () =>
      feFraudDetection
        ? GROUP_OPTIONS_BASE
        : GROUP_OPTIONS_BASE.filter((o) => o.id !== 'bgv_risk_level'),
    [feFraudDetection]
  );
  const BREAKDOWN_OPTIONS: Option[] = useMemo(
    () => [{ id: null, label: 'None' }, ...GROUP_OPTIONS],
    [GROUP_OPTIONS]
  );

  useEffect(() => {
    if (feReportsV3) setCurrentTableColData(initialTableColData);
  }, [initialTableColData, feReportsV3]);

  // ─── Redux & derived state ────────────────────────────────────────────────

  const reduxState = useSelector(
    (state: Record<string, unknown>) =>
      (state as Record<string, unknown>).pipelineReports
  ) as Record<string, unknown>;

  const {
    group: reduxGroup,
    table: reduxData,
    isTableLoading: reduxIsLoading,
    stageMapping: reduxStageMapping,
  } = (reduxState || {}) as {
    group?: { group_by_1?: Option; group_by_2?: Option };
    table?: unknown[];
    isTableLoading?: boolean;
    stageMapping?: Record<string, string>;
  };

  const group = feReportsV3 ? null : reduxGroup;
  const data = feReportsV3 ? [] : reduxData;
  const isLoading = feReportsV3 ? false : reduxIsLoading;
  const stageMapping = feReportsV3 ? {} : reduxStageMapping;

  const { id: groupBy1Id, label: groupBy1Label = 'Time' } = groupBy1 || {};
  const getGroup = (value: Option | undefined, fallback?: Option) =>
    feReportsV3 ? value : value || fallback;

  const drilldownGroup = {
    group_by_1: getGroup(groupBy1, group?.group_by_1),
    group_by_2: getGroup(groupBy2, group?.group_by_2),
  };

  const dataToUse = customData || data || [];

  const stageMappingToUse = Object.keys(customStageMapping || {})?.length
    ? customStageMapping
    : stageMapping;
  const stageCount = Object.keys(stageMappingToUse || {}).length;
  const emptyField = stageCount
    ? Array.from({ length: stageCount }, (_, i) => `s${i + 1}_actual_count`)
    : ['prospect_actual_count', 'application_review_actual_count'];
  const isDataEmpty = isReportTableEmpty(dataToUse, emptyField);

  const { handleDrillDown } = useDrillDownHandler(true, onDrillDownOpen);

  // ─── Column builders ───────────────────────────────────────────────────────

  const createHeaderColumn = (
    baseConfig: Omit<HeaderProps, 'renderFooter'>
  ) => {
    const header = renderHeader({
      ...baseConfig,
      renderFooter: () => <KText size="sm-semi-bold">Total</KText>,
    });
    return {
      ...header,
      id: 'header',
      title: groupBy1Label,
      columnConfig: feReportsV3 ? 'locked' : false,
      Cell: (props: {
        cell: { value: string };
        row: { original: Record<string, unknown> };
      }) => {
        const rawValue = props.cell.value;
        const value =
          groupBy1Id === 'bgv_risk_level'
            ? formatBgvRiskLevel(rawValue)
            : rawValue;
        const row = props.row?.original ?? {};
        const headerList = (
          Array.isArray(row.parentHeader)
            ? row.parentHeader
            : (row.g1_l2 as Array<{ name?: string }> | undefined)
                ?.map((o) => o?.name)
                .filter(Boolean)
        ) as string[] | undefined;
        if (headerList?.length > 1) {
          return (
            <ViewMorePopover
              list={headerList}
              max={1}
              type="office"
              placement="bottom-start"
              trigger="hover"
            />
          );
        }
        if (headerList?.length === 1) {
          return header.Cell({
            ...props,
            cell: { ...props.cell, value: headerList[0] },
          });
        }
        return header.Cell({ ...props, cell: { ...props.cell, value } });
      },
    };
  };

  const createDrilldownCell = (
    row: Record<string, unknown>,
    accessor: string,
    label: string,
    value: unknown,
    isFooter = false
  ) => (
    <Flex
      cursor="pointer"
      _hover={{ textDecoration: 'underline' }}
      onClick={() =>
        handleDrillDown(row, accessor, isFooter, drilldownGroup, label, value)
      }
    >
      {isFooter ? (
        <KText size="sm-semi-bold">{value as ReactNode}</KText>
      ) : (
        (value as ReactNode)
      )}
    </Flex>
  );

  const createTimeColumn = (
    columnAccessor: string,
    latencyKey: string,
    countKey: string,
    width: number
  ) =>
    renderCell({
      title: '',
      accessor: columnAccessor,
      sort: false,
      columnConfig: feReportsV3 ? 'hidden' : true,
      width,
      render: (_, row) => {
        return computeTimeInStage(
          parseInt(row?.[latencyKey] as string) || 0,
          parseInt(row?.[countKey] as string)
        );
      },
      renderFooter: () => '-',
    });

  // ─── Columns (useMemo) ─────────────────────────────────────────────────────

  const columns = useMemo(() => {
    const result: Array<Record<string, unknown>> = [];
    const stageList = Object.values(stageMappingToUse || {});
    const visibility = feReportsV3 ? currentTableColData?.visibility || {} : {};

    const getVisibleDynamic = (count: number) =>
      feReportsV3
        ? Array.from({ length: count }, (_, i) => i).filter(
            (i) => visibility[`s${i + 1}_actual_count`] !== false
          )
        : Array.from({ length: count }, (_, i) => i);

    const getVisibleStatic = () =>
      feReportsV3
        ? STATIC_STAGES.map((s, i) =>
            visibility[s.accessor] !== false ? i : -1
          ).filter((i) => i >= 0)
        : STATIC_STAGES.map((_, i) => i);

    if (stageList.length) {
      // Dynamic stage mapping from API
      const visibleStages = getVisibleDynamic(stageList.length);
      const header = createHeaderColumn({
        accessor: 'header',
        title: groupBy1Label ?? 'Time',
        width: isReadOnly ? 200 : 250,
        sort: true,
        groupBy: groupBy1Id,
      });
      result.push(header);

      stageList.forEach((stage, idx) => {
        const accessor = `s${idx + 1}_actual_count`;
        result.push({
          ...renderCell({
            title: stage as string,
            accessor,
            width: 180,
            sort: true,
            columnConfig: feReportsV3 ? 'skipreorder' : true,
            render: (_, row) =>
              createDrilldownCell(
                row,
                accessor,
                stage as string,
                row?.[accessor]
              ),
            renderFooter: (info) => {
              const row = info?.rows?.[0]?.original as Record<string, unknown>;
              return createDrilldownCell(
                row,
                accessor,
                stage as string,
                row?.dynamic_total?.[idx],
                true
              );
            },
          }),
          id: accessor,
          ...(feReportsV3 && { relatedColumns: [`pipeline_metric_${idx}`] }),
        });

        const nextIdx = visibleStages.indexOf(idx) + 1;
        if (nextIdx > 0 && nextIdx < visibleStages.length) {
          result.push({
            ...createTimeColumn(
              `pipeline_metric_${idx}`,
              `s${idx + 1}_latency`,
              `s${idx + 1}_actual_count`,
              180
            ),
            id: `pipeline_metric_${idx}`,
          });
        }
      });
    } else {
      // Static stage structure (default)
      const visibleStages = getVisibleStatic();
      const header = createHeaderColumn({
        accessor: 'header',
        title: groupBy1Label ?? 'Time',
        width: isReadOnly ? 150 : 200,
        sort: true,
        groupBy: groupBy1Id,
        breakDownBy: groupBy2?.id,
        isReadOnly,
      });
      result.push(header);

      STATIC_STAGES.forEach((stage, idx) => {
        result.push({
          ...renderCell({
            title: stage.title,
            accessor: stage.accessor,
            width: idx === 0 ? 180 : isReadOnly ? 120 : 180,
            sort: true,
            columnConfig: feReportsV3 ? 'skipreorder' : true,
            render: (_, row) =>
              createDrilldownCell(
                row,
                stage.accessor,
                stage.title,
                row?.[stage.accessor]
              ),
            renderFooter: (info) => {
              const row = info?.rows?.[0]?.original as Record<string, unknown>;
              return createDrilldownCell(
                row,
                stage.accessor,
                stage.title,
                row?.[`${stage.accessor}_total`],
                true
              );
            },
          }),
          id: stage.accessor,
          ...(feReportsV3 && {
            relatedColumns: [`pipeline_metric_${idx + 1}`],
          }),
        });

        const hasNext =
          visibleStages.indexOf(idx) >= 0 &&
          visibleStages.indexOf(idx) < visibleStages.length - 1;
        if (hasNext && stage.timeAccessor) {
          result.push({
            ...createTimeColumn(
              `pipeline_metric_${idx + 1}`,
              stage.timeAccessor,
              stage.accessor,
              isReadOnly ? 95 : 180
            ),
            id: `pipeline_metric_${idx + 1}`,
          });
        }
      });
    }

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    groupBy1Label,
    groupBy1Id,
    groupBy2?.id,
    stageMappingToUse,
    feReportsV3,
    currentTableColData,
    isReadOnly,
  ]);

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleSortChange = (state: {
    sortBy?: Array<{ id: string; desc?: boolean }>;
  }) => {
    (onSortChange as (_arg?: unknown) => void)(state?.sortBy?.[0]);
  };

  const handleBreakdownChange = (key: string, option: Option) => {
    if (key === 'group_by_1')
      dispatch(applyGroup({ key: 'group_by_1', value: option }));
    else if (key === 'group_by_2')
      dispatch(applyGroup({ key: 'group_by_2', value: option }));
    else if (key === 'metrics') setMetric?.(option.id as string);
  };

  const handleTableColChange = (tableColData: Record<string, unknown>) => {
    setCurrentTableColData(tableColData);
    onTableColDataChange?.(tableColData);
  };

  const breakdownConfig = [
    {
      key: 'group_by_1',
      prefix: 'Group by',
      options: GROUP_OPTIONS,
      selected: feReportsV3
        ? groupBy1 || GROUP_OPTIONS[0]
        : groupBy1 || group?.group_by_1 || GROUP_OPTIONS[0],
    },
    {
      key: 'group_by_2',
      prefix: 'Breakdown by',
      options: BREAKDOWN_OPTIONS,
      selected: feReportsV3
        ? groupBy2 || BREAKDOWN_OPTIONS[0]
        : groupBy2 || group?.group_by_2 || BREAKDOWN_OPTIONS[0],
    },
  ];

  // ─── Render ───────────────────────────────────────────────────────────────

  if (isFilterApplied) {
    return (
      <EmptyReportChart
        text="Filters not applicable"
        subText="The applied filters are not applicable for this block"
      />
    );
  }

  const marginRight = isPrintView
    ? '0'
    : isDataEmpty
      ? '35px'
      : isEditMode
        ? '55px'
        : '35px';

  return (
    <Box h="calc(100% - 25px)" w="100%">
      {consumer === 'DEFAULT' &&
        (() => {
          const breakdownItems = breakdownConfig.map(
            ({ key, prefix, options, selected }) => (
              <BreakdownBy
                key={key}
                isPortal={!isFullScreen}
                prefix={prefix}
                options={options}
                selected={selected}
                isEditMode={isEditMode}
                onChange={(option: Option) =>
                  customBreakdownChange
                    ? customBreakdownChange(key, option)
                    : handleBreakdownChange(key, option)
                }
              />
            )
          );
          const metricsItem = (
            <Flex
              alignItems="center"
              border="1px solid"
              borderColor="border-color"
              borderRadius="md"
              px="3"
              py="2"
              height="34px"
            >
              <KText size="sm-regular" color="secondary-700">
                Metrics:{' '}
              </KText>
              <KText size="sm-semi-bold" color="secondary-700">
                {
                  (
                    passThroughMetric ||
                    LIVE_PIPELINE_METRICS.find((m) => m.id === metric) ||
                    LIVE_PIPELINE_METRICS[0]
                  )?.label
                }
              </KText>
            </Flex>
          );

          if (isPrintView) {
            // In print view, flatten controls as direct siblings of the title
            // so they can flex-wrap individually. Title stays on row 1 (flex=1)
            // with as many buttons as fit; remaining buttons wrap to row 2,
            // right-aligned via justifyContent="end".
            return (
              <Flex
                alignItems="flex-start"
                justifyContent="end"
                flexWrap="wrap"
                rowGap="8px"
                columnGap="8px"
                position="sticky"
                top="0"
                zIndex="6"
                bg="white"
                mb="8px"
              >
                {renderTitle?.()}
                {breakdownItems}
                {metricsItem}
              </Flex>
            );
          }

          return (
            <Flex
              alignItems="flex-start"
              justifyContent="end"
              position="sticky"
              top="0"
              zIndex="6"
              bg="white"
            >
              {renderTitle?.()}
              <Flex
                gap="8px"
                alignItems="center"
                justifyContent="end"
                mb="8px"
                mr={marginRight}
                data-test-id="section-pipeline-controls"
              >
                {breakdownItems}
                {metricsItem}
              </Flex>
            </Flex>
          );
        })()}

      {isLoading ? (
        <CenterSpinner h="400px" />
      ) : (
        <EmptyDataWrapper data={dataToUse} field={emptyField}>
          <Box h="100%" w="100%" overflow="hidden !important">
            <Table
              tableId="live-job-pipeline-table"
              skipLocalStorage={skipLocalStorage}
              isColumnLevelSorting
              defaultSortBy={[{ id: 'header', desc: false }]}
              enableColumnConfig={isEditMode}
              columns={columns}
              data={dataToUse}
              enableFooter
              layoutScrollContainerStyles="c-data-table wide-content table-container"
              enableExpanded
              layoutStyles={{ borderBottom: 'none' }}
              getTableState={handleSortChange}
              tableColStyles={{
                ...tableColStyles,
                hideReorderColumns: feReportsV3,
              }}
              onTableColDataChange={
                feReportsV3 ? handleTableColChange : onTableColDataChange
              }
              initialTableColData={initialTableColData}
            />
          </Box>
        </EmptyDataWrapper>
      )}
    </Box>
  );
};
